import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UserPlus, Search, FileSpreadsheet, Pencil, Trash2, Eye, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadExcel, pad } from "@/lib/api";
import { RT_LIST, KELOMPOK, EKONOMI } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/StatCard";
import { FieldSelect } from "@/components/FieldSelect";
import { WargaForm } from "@/components/WargaForm";
import { WargaDetail } from "@/components/WargaDetail";
import { OapBadge, EkoBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function DataWarga() {
  const { isAdmin } = useAuth();
  const [params] = useSearchParams();
  const [filters, setFilters] = useState({ rt: params.get("rt") || "", search: "", oap: "", ekonomi: "", kelompok: "", status: "Aktif" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const p = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ""));
    if (!filters.status) p.status = "";
    api.get("/warga", { params: p }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const setF = (k) => (v) => setFilters((p) => ({ ...p, [k]: v }));
  const remove = async () => {
    try {
      await api.delete(`/warga/${toDelete.id}`);
      toast.success("Data warga dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };

  return (
    <div data-testid="data-warga-page">
      <PageHeader kicker={filters.rt ? `RT ${pad(filters.rt)}` : "Semua RT"} title="Data Warga">
        <Button variant="outline" onClick={() => downloadExcel(filters.rt).catch((e) => toast.error(errMsg(e)))} data-testid="warga-export-button">
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Ekspor Excel
        </Button>
        {isAdmin && (
          <Button className="bg-[#0b4f4a] hover:bg-[#072f2c]" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-warga-button">
            <UserPlus className="h-4 w-4 mr-2" /> Tambah Warga
          </Button>
        )}
      </PageHeader>

      {!isAdmin && (
        <div className="mb-4 flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2.5" data-testid="view-only-banner">
          <Lock className="h-4 w-4" /> Mode lihat — akun Lurah tidak dapat menambah, mengubah, atau menghapus data.
        </div>
      )}

      <div className="bg-white border rounded-xl p-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
        <div className="relative col-span-2 md:col-span-3 xl:col-span-2">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cari nama, NIK, atau No. KK" value={filters.search} onChange={(e) => setF("search")(e.target.value)} className="pl-9" data-testid="warga-search-input" />
        </div>
        <FieldSelect value={filters.rt} onChange={setF("rt")} options={RT_LIST.map((n) => ({ value: n, label: `RT ${pad(n)}` }))} allLabel="Semua RT" testid="filter-rt" />
        <FieldSelect value={filters.oap} onChange={setF("oap")} options={["OAP", "Non-OAP"]} allLabel="OAP & Non-OAP" testid="filter-oap" />
        <FieldSelect value={filters.ekonomi} onChange={setF("ekonomi")} options={EKONOMI} allLabel="Semua Ekonomi" testid="filter-ekonomi" />
        <FieldSelect value={filters.kelompok} onChange={setF("kelompok")} options={KELOMPOK} allLabel="Semua Usia" testid="filter-kelompok" />
        <FieldSelect value={filters.status} onChange={setF("status")} options={["Aktif", "Meninggal", "Pindah"]} allLabel="Semua Status" testid="filter-status" />
      </div>

      <div className="text-xs text-slate-500 mt-4 mb-2" data-testid="warga-count">{loading ? "Memuat…" : `${rows.length} warga ditemukan`}</div>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="warga-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              {["Nama", "NIK / No. KK", "RT", "L/P", "Usia", "Hubungan", "Pekerjaan", "OAP", "Ekonomi", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.id} className="border-t hover:bg-amber-50/40 transition-colors" data-testid={`warga-row-${w.nik}`}>
                <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">{w.nama}{w.status !== "Aktif" && <span className="ml-2 text-[10px] text-rose-600">({w.status})</span>}</td>
                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{w.nik}<div className="text-slate-400">{w.no_kk}</div></td>
                <td className="px-4 py-3">{pad(w.rt)}</td>
                <td className="px-4 py-3">{w.jenis_kelamin === "Laki-laki" ? "L" : "P"}</td>
                <td className="px-4 py-3 whitespace-nowrap">{w.usia} th<div className="text-[11px] text-slate-500">{w.kelompok_usia}</div></td>
                <td className="px-4 py-3 whitespace-nowrap">{w.shdk}</td>
                <td className="px-4 py-3 whitespace-nowrap">{w.pekerjaan}</td>
                <td className="px-4 py-3"><OapBadge v={w.status_oap} /></td>
                <td className="px-4 py-3"><EkoBadge v={w.status_ekonomi} /></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <IconBtn icon={Eye} onClick={() => setDetail(w)} testid={`view-warga-${w.nik}`} />
                    {isAdmin && <IconBtn icon={Pencil} onClick={() => { setEditing(w); setFormOpen(true); }} testid={`edit-warga-${w.nik}`} />}
                    {isAdmin && <IconBtn icon={Trash2} danger onClick={() => setToDelete(w)} testid={`delete-warga-${w.nik}`} />}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-14 text-center text-slate-500" data-testid="warga-empty">Belum ada data warga untuk filter ini.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <WargaForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSaved={load} />
      <WargaDetail w={detail} onOpenChange={() => setDetail(null)} />
      <AlertDialog open={Boolean(toDelete)} onOpenChange={() => setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus data warga?</AlertDialogTitle>
            <AlertDialogDescription>
              Data {toDelete?.nama} ({toDelete?.nik}) akan dihapus permanen. Untuk warga meninggal atau pindah, gunakan menu Mutasi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm-button">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const IconBtn = ({ icon: Icon, onClick, testid, danger }) => (
  <button onClick={onClick} data-testid={testid}
    className={`h-8 w-8 grid place-items-center rounded-lg border transition-colors ${danger ? "hover:bg-rose-50 hover:text-rose-600" : "hover:bg-slate-100"}`}>
    <Icon className="h-4 w-4" />
  </button>
);
